import React, { useMemo } from 'react';
import clsx from 'clsx';
import type { ReleaseThemeId } from 'fym-shared/src/music24/releaseThemes';
import type { AssetStyleId, WaveformShape } from 'fym-shared/src/music24/assetStyles';
import { artworkMotionBarGap, artworkMotionBarStyle } from 'fym-shared/src/music24/story/artworkMotionVideo';
import type { StoryColours } from 'fym-shared/src/music24/story/storyStyles';
import { visualizerPose, type KitVisualizerVideo } from 'fym-shared/src/music24/story/visualizerVideo';
import { TypedMemo } from '../../../../common/types';
import type { FrameSong } from './assetFrameVars';
import { frameThemeClass } from './frameClasses';
import { frameBars, frameSeed } from './frameShapes';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the frames; each frame uses its own slice of it */
import styles from './AssetFrame.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface VisualizerFrameProps {
  themeId: ReleaseThemeId;
  style: AssetStyleId;
  /** Kept for the callers; design z2 draws the same 16 bars for every style (no ring). */
  waveform?: WaveformShape | null;
  song: FrameSong;
  vars: React.CSSProperties;
  className?: string;
}

/** The colours of the frame as CSS variables (the bar styles of fym-shared take colour strings). */
const VAR_COLOURS: StoryColours = {
  ink: 'var(--g)',
  surface: 'var(--s)',
  text: 'var(--t)',
  accent: 'var(--a)',
  onAccent: 'var(--oa)',
};

/** A length of the 1920 px canvas as a share of the frame width (the frame is a size container). */
const cqw = (px: number): string => `${((px / 1920) * 100).toFixed(3)}cqw`;

/**
 * A still of the visualizer on its stage (design `asset-redesign/z2`), drawn from the pose that the
 * renderer uses (`visualizerPose` of fym-shared, a normal section): the framed cover in the
 * centre, 16 bars in the theme shape under it and the id line. No ring in any theme.
 */
const VisualizerFrameComponent: React.FC<VisualizerFrameProps> = ({ themeId, style, song, vars, className }) => {
  const video = useMemo<KitVisualizerVideo>(
    () => ({
      kind: 'visualizer',
      title: song.title,
      artist: song.artist,
      cta: song.kicker,
      link: null,
      motion: style === 'pulse' ? 'pulse' : 'drift',
      barSec: 2,
      titleEndSec: 8,
      endCardSec: 1e9,
      durationSec: 1e9,
      levels: [{ startSec: 0, level: 0 }],
      posterSec: 0.5,
    }),
    [song.artist, song.kicker, song.title, style]
  );
  const pose = visualizerPose(30, video, themeId, { kick: 0, energy: 0.6 });
  const levels = useMemo(() => frameBars(16, frameSeed(song.title)), [song.title]);
  const barScale = pose.bars.h / 110;

  return (
    <div
      className={clsx(styles.fv, frameThemeClass(themeId), className)}
      style={vars}
      aria-hidden="true"
      data-frame="visualizer"
      data-style={style}
    >
      <i className={styles.g} />
      <i
        className={styles.maCover}
        style={{ left: cqw(pose.cover.x), top: cqw(pose.cover.y), width: cqw(pose.cover.w) }}
      />
      <span
        data-testid="visualizer-frame-bars"
        style={{
          left: cqw(pose.bars.x),
          top: cqw(pose.bars.y),
          width: cqw(pose.bars.w),
          height: cqw(pose.bars.h),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: cqw(artworkMotionBarGap(themeId) * barScale),
        }}
      >
        {levels.map((bar, index) => {
          const level = Math.min(1, Math.max(0, parseFloat(String(bar.style.height ?? '50')) / 100));
          const css = artworkMotionBarStyle(themeId, VAR_COLOURS, index, level, pose.bars.h);
          return (
            <i
              key={bar.key}
              style={{
                ...css,
                position: 'static',
                width: cqw(Number(css.width ?? 14)),
                height: cqw(Number(css.height ?? 20)),
                boxShadow: undefined,
              }}
            />
          );
        })}
      </span>
      <span
        style={{
          left: cqw(pose.id.x),
          top: cqw(pose.id.y),
          width: cqw(pose.id.w),
          textAlign: 'center',
          whiteSpace: 'nowrap',
          color: 'var(--t)',
          fontSize: cqw(56),
        }}
      >
        <span style={{ fontFamily: 'var(--df)', fontWeight: 'var(--dw)' as React.CSSProperties['fontWeight'] }}>
          {song.title}
        </span>
        <span style={{ fontFamily: 'var(--tf)', fontSize: cqw(36), marginLeft: cqw(24) }}>{song.artist}</span>
      </span>
    </div>
  );
};

export const VisualizerFrame = TypedMemo(VisualizerFrameComponent);
